
import { Component, Vue, Prop } from "vue-property-decorator";

import { Config, Logger, LogLevel } from "../services";
import { Utils } from "../util";

@Component
export default class ReCaptcha extends Vue {
  private readonly id = Utils.GetGuid();

  private response: string | null = null;

  private responseWaiter: ((value?: string | PromiseLike<string> | undefined) => void) | null = null;

  private siteKey = Config.Website.Settings!.ReCaptcha.ReCaptchaSiteKey;

  private recaptchaVersion = Config.Website.Settings!.ReCaptcha.ReCaptchaVersion;

  get callbackFunctionName() {
    return `_recaptcha_callback_${this.id}`;
  }

  mounted() {
    (window as any)[this.callbackFunctionName] = (response: string | null) => {
      Logger.Log(LogLevel.Debug, '[ReCaptcha]', 'Got response');

      this.response = response || null;

      if(this.responseWaiter !== null && this.response !== null) {
        this.responseWaiter(this.response);
        this.responseWaiter = null;
      }
    }

    this.loadLibrary().then(() => {
      Logger.Log(LogLevel.Info, '[ReCaptcha]', 'Loaded ReCaptcha');
    }).catch(() => {
      Logger.Log(LogLevel.Error, '[ReCaptcha]', 'Failed to load ReCaptcha');
    });

    Logger.Log(LogLevel.Info, '[ReCaptcha]', 'Registered ReCaptcha', this.id);
  }

  reset() {
    (window as any).grecaptcha.reset();
  }

  getResponse(): Promise<string> {
    return new Promise((resolve, reject) => {
      if(this.response !== null && this.response.length > 0) {
        resolve(this.response);
        return;
      }

      this.responseWaiter = () => {
        Logger.Log(LogLevel.Debug, '[ReCaptcha]', 'Resolving response');
        resolve();
      };

      const win = window as any;

      try {
        win.grecaptcha.execute();
      } catch (ex) {
        Logger.Log(LogLevel.Error, '[ReCaptcha]', 'Error executing recaptcha', ex)
        win.grecaptcha.render(`recaptcha_${this.id}`);
        win.grecaptcha.execute();
      }
    });
  }

  loadLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if((window as any).grecaptcha !== undefined) {
        Logger.Log(LogLevel.Debug, '[ReCaptcha]', 'ReCaptcha is already defined.');

        resolve(true);
        return;
      }
      
      const scr = document.createElement('script');

      (window as any).pl_recaptcha_loaded = () => {
        resolve(true);
      }

      scr.onerror = () => {
        reject();
      }

      scr.async = true;
      scr.defer = true;

      document.head.appendChild(scr);

      scr.src = 'https://www.google.com/recaptcha/api.js?onload=pl_recaptcha_loaded';
    });
  }
}
